import React, { useState, useEffect } from "react";
import { Activity, TrendingUp, Users, Star, CheckCircle, AlertCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "https://api.littleexplorers.help";

const OrgAnalyticsPanel = ({ orgId, getAccessToken }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orgId) return;

    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = getAccessToken();
        const response = await fetch(
          `${API_URL}/v2/organizations/${orgId}/health-score`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status}`);
        }

        const data = await response.json();
        setAnalytics(data);
      } catch (err) {
        console.error("Error fetching org analytics:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [orgId, getAccessToken]);

  if (loading) {
    return (
      <div className="p-4 bg-gray-800 rounded-lg flex items-center gap-2 text-gray-400">
        <Activity className="w-4 h-4 animate-spin" />
        Loading analytics...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg text-red-400">
        <AlertCircle className="w-5 h-5 inline mr-2" />
        Error loading analytics: {error}
      </div>
    );
  }

  if (!analytics) return null;

  const { overallScore, healthStatus, categories, details, quickWins } = analytics;

  const getStatusColor = (status) => {
    switch (status) {
      case "excellent": return "text-green-400";
      case "good": return "text-blue-400";
      case "fair": return "text-yellow-400";
      case "poor": return "text-orange-400";
      case "critical": return "text-red-400";
      default: return "text-gray-400";
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case "excellent": return "bg-green-500/20 border-green-500/30";
      case "good": return "bg-blue-500/20 border-blue-500/30";
      case "fair": return "bg-yellow-500/20 border-yellow-500/30";
      case "poor": return "bg-orange-500/20 border-orange-500/30";
      case "critical": return "bg-red-500/20 border-red-500/30";
      default: return "bg-gray-500/20 border-gray-500/30";
    }
  };

  return (
    <div className="space-y-4">
      {/* Overall Health Score */}
      <div className={`p-4 rounded-lg border ${getStatusBg(healthStatus)}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity className={`w-6 h-6 ${getStatusColor(healthStatus)}`} />
            <div>
              <h3 className="font-bold text-white text-lg capitalize">
                {healthStatus} Health
              </h3>
              <p className="text-sm text-gray-400">
                Overall Score: <span className={`font-bold ${getStatusColor(healthStatus)}`}>{overallScore}/100</span>
              </p>
            </div>
          </div>
          <div className={`text-3xl font-bold ${getStatusColor(healthStatus)}`}>
            {overallScore}
          </div>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Users className="w-4 h-4" />
            Programs
          </div>
          <div className="text-xl font-bold text-white">{details.programs.totalCount}</div>
        </div>
        
        <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Activity className="w-4 h-4" />
            Active
          </div>
          <div className="text-xl font-bold text-white">{details.programs.activeCount}</div>
        </div>
        
        <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <Star className="w-4 h-4" />
            Curated
          </div>
          <div className="text-xl font-bold text-yellow-400">
            {details.discoverability.isCurated ? "Yes" : "No"}
          </div>
        </div>
        
        <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <TrendingUp className="w-4 h-4" />
            Sessions
          </div>
          <div className="text-xl font-bold text-white">{details.sessions.totalCount}</div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="bg-gray-800/30 p-4 rounded-lg border border-gray-700">
        <h4 className="text-white font-medium mb-3 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-indigo-400" />
          Health Breakdown
        </h4>
        <div className="space-y-2">
          {Object.entries(categories).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between">
              <span className="text-gray-400 text-sm capitalize">{key}</span>
              <div className="flex items-center gap-2">
                <div className="w-24 bg-gray-700 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      value.percentage >= 80 ? 'bg-green-500' :
                      value.percentage >= 60 ? 'bg-blue-500' :
                      value.percentage >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${value.percentage}%` }}
                  />
                </div>
                <span className="text-white text-sm font-medium w-12 text-right">
                  {value.score}/{value.maxScore}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Wins */}
      {quickWins.length > 0 && (
        <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-4">
          <h4 className="text-white font-medium mb-2 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-indigo-400" />
            Quick Wins
          </h4>
          <ul className="space-y-1">
            {quickWins.slice(0, 3).map((win, idx) => (
              <li key={idx} className="text-indigo-300 text-sm flex items-start gap-2">
                <span className="text-indigo-500 mt-1">•</span>
                {win}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default OrgAnalyticsPanel;
